import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
} from 'vue';

import type { Ref } from 'vue';

export type SizeType = 'offset' | 'client' | 'scroll' | 'border-box';

/**
 * Типы размеров:
 * - offset: content + padding + border (по умолчанию)
 * - client: content + padding (без border и scrollbar)
 * - scroll: полный размер контента включая скрытую часть
 * - border-box: точные размеры из getBoundingClientRect
 */
export function useElementSize (
  elementRef: Ref<HTMLElement | null>,
  options: { type?: SizeType } = {},
) {
  const { type = 'border-box' } = options;
  const width = ref<number>(0);
  const height = ref<number>(0);

  const updateSize = () => {
    if (!elementRef.value) {
      return;
    }

    const element = elementRef.value;

    switch (type) {
      case 'offset':
        width.value = element.offsetWidth;
        height.value = element.offsetHeight;
        break;
      case 'client':
        width.value = element.clientWidth;
        height.value = element.clientHeight;
        break;
      case 'scroll':
        width.value = element.scrollWidth;
        height.value = element.scrollHeight;
        break;
      case 'border-box': {
        const rect = element.getBoundingClientRect();
        width.value = rect.width;
        height.value = rect.height;
        break;
      }
    }
  };

  let resizeObserver: ResizeObserver | null = null;

  const initResizeObserver = () => {
    if (typeof ResizeObserver !== 'undefined' && elementRef.value) {
      resizeObserver = new ResizeObserver(() => {
        updateSize();
      });
      resizeObserver.observe(elementRef.value);
    } else {
      window.addEventListener('resize', updateSize);
    }
  };

  const cleanup = () => {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    } else {
      window.removeEventListener('resize', updateSize);
    }
  };

  onMounted(async () => {
    await nextTick();
    updateSize();
    initResizeObserver();
  });

  onUnmounted(() => {
    cleanup();
  });

  return {
    width: computed(() => width.value),
    height: computed(() => height.value),
    updateSize,
  };
}

export function useElementHeight (
  elementRef: Ref<HTMLElement | null>,
  options: { type?: SizeType } = {},
) {
  const { height } = useElementSize(elementRef, options);
  return height;
}

export function useElementWidth (
  elementRef: Ref<HTMLElement | null>,
  options: { type?: SizeType } = {},
) {
  const { width } = useElementSize(elementRef, options);
  return width;
}
